<template>
  <div class="station-performance-container">
    <div class="perf-panel-bar">
      <div class="perf-panel-segment overdue" :style="overdueBarStyle" v-if="stationPerfData.overduePanelCount > 0"></div>
      <div class="perf-panel-segment today" :style="dueTodayBarStyle" v-if="stationPerfData.dueTodayPanelCount > 0"></div>
      <div class="perf-panel-segment future" :style="dueAheadBarStyle" v-if="stationPerfData.dueAheadPanelCount > 0"></div>
    </div>
    <div class="perf-panel-legend row">
      <div class="col-4" v-if="stationPerfData.overduePanelCount > 0">
        <div class="legend-dot overdue"></div> {{stationPerfData.overduePanelCount}} Overdue
      </div>
      <div class="col-4" v-if="stationPerfData.dueTodayPanelCount > 0">
        <div class="legend-dot today"></div> {{stationPerfData.dueTodayPanelCount}} Today
      </div>
      <div class="col-4" v-if="stationPerfData.dueAheadPanelCount > 0">
        <div class="legend-dot future"></div> {{stationPerfData.dueAheadPanelCount}} Due Ahead
      </div>
    </div>
    <div class="perf-panel-legend row">
      <div class="col-4" v-if="stationPerfData.completedTodayCount > 0">
        <i class="fas fa-check-circle icon completed"></i> {{stationPerfData.completedTodayCount}} Completed
      </div>
      <div class="col-5" v-if="stationPerfData.completedTodayUnits > 0">
        <i class="fas fa-ruler-combined icon completed"></i> <UOMValue :uomType="stationPerfData.uomType" :value="stationPerfData.completedTodayUnits" :precision="2" /> Completed
      </div>
      <div class="col-3" v-if="stationPerfData.recutTodayCount > 0">
        <i class="fas fa-times-circle icon recut"></i> {{stationPerfData.recutTodayCount}} Recut
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.station-performance-container {
  padding: 8px;
  width: 100%;
  height: 100%;
}
.perf-panel-bar {
  margin: 0;
  padding: 0;
  height: 24px;
  display: flex;
  flex-direction: row;
}
.perf-panel-segment {
  flex: 0.33;
}
.perf-panel-segment:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.perf-panel-segment:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.overdue {
  background-color: $chart-colour-bad;
}
.today {
  background-color: $chart-colour-medium;
}
.future {
  background-color: $chart-colour-good;
}
.completed {
  color: $chart-colour-good;
}
.recut {
  color: $chart-colour-bad;
}
.perf-panel-legend {
  font-size: 13px;
  font-weight: bold;
  padding: 3px 4px 0px 4px;
}
.perf-panel-legend .icon {
  margin-right: 4px;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 4px;
}
</style>

<script>
import moment from 'moment'
import timerMixin from '@/mixins/timer'

export default {
  name: 'StationPerformanceGraph',
  mixins: [timerMixin],
  props: {
    productionStation: Object
  },
  components: {
  },
  data() {
    return {
      lastUpdateTime: moment(),
      stationPerfData: {
        uomType: '',
        overduePanelCount: 0,
        dueTodayPanelCount: 0,
        dueAheadPanelCount: 0,
        completedTodayCount: 0,
        completedTodayUnits: 0,
        recutTodayCount: 0
      }
    }
  },
  computed: {
    totalSchedulePanelCount() {
      return this.stationPerfData.overduePanelCount + this.stationPerfData.dueTodayPanelCount + this.stationPerfData.dueAheadPanelCount
    },
    overdueBarStyle() {
      if(this.totalSchedulePanelCount == 0)
        return {}

      return {
        flex: this.stationPerfData.overduePanelCount / this.totalSchedulePanelCount
      }
    },
    dueTodayBarStyle() {
      if(this.totalSchedulePanelCount == 0)
        return {}
      
      return {
        flex: this.stationPerfData.dueTodayPanelCount / this.totalSchedulePanelCount
      }
    },
    dueAheadBarStyle() {
      if(this.totalSchedulePanelCount == 0)
        return {}
      
      return {
        flex: this.stationPerfData.dueAheadPanelCount / this.totalSchedulePanelCount
      }
    }
  },
  mounted() {
    this.requestChartData()
    this.createIntervalTimer('performanceGraphUpdate', this.checkUpdateTime, 1000 * 60)

    this.subscribeEndpoint('/production/panelschedule', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/panelschedule')
  },
  methods: {
    requestChartData() {
      // Flag last update time
      // Add in randomness to refresh timer so all stations are not updating simultaneously in the factory
      this.lastUpdateTime = moment().add(Math.abs(Math.random() * 120), 'seconds')

      this.$apiService.glassProductionStationService.getProductionStationPerformance(this.productionStation.id)
      .then(response => {

        this.stationPerfData.overduePanelCount = response.data.schedule.overduePanelCount
        this.stationPerfData.dueTodayPanelCount = response.data.schedule.dueTodayPanelCount
        this.stationPerfData.dueAheadPanelCount = response.data.schedule.dueAheadPanelCount

        this.stationPerfData.uomType = response.data.stationUOMType
        this.stationPerfData.completedTodayCount = response.data.panelsCompleted
        this.stationPerfData.completedTodayUnits = response.data.unitsCompleted
        this.stationPerfData.recutTodayCount = response.data.panelsRecut
      })

    },
    reloadData() {
      // Reload chart data
      // Called from station app. Usually when panel schedule list has been updated (ie, scan in/out's have occured)
      this.requestChartData()
    },

    checkUpdateTime() {
      let updateIntervalMins = 10

      // Check if we should update the graph. A few minutes after the last update we'll refresh the graph
      // Ensures the graph stays up to date if no scanning occurs or the performance stats page updates while left open
      if(this.lastUpdateTime.clone().add(updateIntervalMins, 'minutes').isBefore(moment())) {
        this.requestChartData()
      }
    },
  }
}
</script>